// =============================================================================
// Ferramentas e assinaturas — a planilha do Financeiro dentro do Nexa
//
// Pedido de 21/10: "tem que ficar igual à planilha" — as colunas são as da
// planilha "Controle Ferramentas CF", na mesma ordem. As abas ficaram quatro em
// 22/10 (068), por decisão do Enzo:
//
//   🗂 Controle Geral · Cancelados · ⚠️ Pendências · 💰 Painel Financeiro
//
// "Novas dos CSVs" virou pendência do tipo "cobrança sem ferramenta", e
// "Instruções" saiu: era o manual da planilha. As Atenções críticas dela estão
// em Pendências, como críticas.
//
// Duas diferenças da planilha, e as duas são o Nexa fazendo o que ela fazia à mão:
//   · a ferramenta cancelada fica em Cancelados, venha de onde vier (na
//     planilha, algumas canceladas continuavam no meio do Controle Geral);
//   · o Painel Financeiro é calculado a partir do cadastro, e não digitado.
//
// Excluir (078, pedido de 27/10) é para a linha que não deveria existir — o
// Bubble tem 4 linhas na planilha para 2 cobranças. Ela sai de todas as telas e
// contas, e a planilha não a traz de volta; fica em "Excluídas", no fim da aba
// Cancelados, para restaurar. Ferramenta que existiu e foi encerrada se cancela.
//
// Os termos de busca (do "Nome no Cartão / Extrato") são o que liga a
// ferramenta às cobranças do Conta Azul; o reconhecimento automático e os
// alertas vêm na próxima etapa.
//
// ⚠️ Login e senha (o grupo ACESSO): no cofre da 066, cifrados pela aplicação.
// Só a administração vê, uma ferramenta por vez, e cada vez fica no Histórico.
// Para os outros, as duas colunas mostram só o cadeado.
// =============================================================================

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api, buscarFerramentas, excluirFerramenta, FalhaApi, reconhecerCobrancas, restaurarFerramenta, revelarAcessoFerramenta,
  salvarAcessoFerramenta, salvarFerramenta,
  type CandidataFerramenta, type DadosFerramentas, type Ferramenta, type FerramentaEdicao, type FerramentaExcluida,
} from "../lib/api.ts";
import { dataCurta, moeda } from "../lib/formato.ts";
import { derivarCampos } from "../lib/ferramentasPlanilha.ts";
import {
  calcularCiclo, CICLOS, TEXTO_CICLO, textoCotacao, valorDaPlanilha, type Ciclo, type Cotacao,
} from "../lib/ferramentaCiclo.ts";
import { FAMILIAS, mesFechado, ROTULO_FAMILIA, type Familia } from "../lib/ferramentasAnalise.ts";
import { COR_STATUS, dolar, ROTULO_FORMA, ROTULO_STATUS_FERRAMENTA, rotuloStatus, textoForma } from "../lib/ferramentas.ts";
import { Painel, Vazio } from "../componentes/ui.tsx";
import {
  AlcaLargura, CaixaGrade, CampoCompacto, CLASSE_BOTAO_BARRA, FiltrosRecolhiveis, NomeNaLargura, useLarguraColuna,
} from "../componentes/Grade.tsx";
import { CampoDinheiro } from "../componentes/CampoDinheiro.tsx";
import { Modal } from "../componentes/Modal.tsx";
import { AbaPendencias } from "./FerramentasAbas.tsx";
import { AbaPainel } from "./FerramentasPainel.tsx";

const textoRenovacao = (f: Ferramenta) =>
  f.texto_planilha?.renovacao ?? (f.renovacao === "automatica" ? "S" : f.renovacao === "manual" ? "Não" : "");

type CampoValor = "valor_mensal_centavos" | "valor_trimestral_centavos" | "valor_semestral_centavos"
  | "valor_anual_centavos" | "valor_total_ano_centavos" | "valor_usd_centavos";
/** O valor da coluna; sem número, o texto que a planilha tinha ("Variado"). */
function Valor({ f, campo }: { f: Ferramenta; campo: CampoValor }) {
  const v = f[campo];
  if (v) return <>{campo === "valor_usd_centavos" ? dolar(v) : moeda(v)}</>;
  const t = f.texto_planilha?.[campo];
  return t ? <span className="text-tinta-media italic">{t}</span> : null;
}

type ColunaValor = "mensal" | "trimestral" | "semestral" | "anual" | "total" | "usd";
const CAMPO_DA_PLANILHA: Record<ColunaValor, CampoValor> = {
  mensal: "valor_mensal_centavos", trimestral: "valor_trimestral_centavos", semestral: "valor_semestral_centavos",
  anual: "valor_anual_centavos", total: "valor_total_ano_centavos", usd: "valor_usd_centavos",
};
/**
 * Com ciclo (067), as colunas de valor saem dele: o mensal equivalente, o do
 * ciclo na coluna do ciclo, o custo no ano em R$ Anual e em Total (ano), e o
 * dólar em Valor USD. Sem ciclo, os valores da planilha, como vieram.
 */
function ValorDaColuna({ f, coluna }: { f: Ferramenta; coluna: ColunaValor }) {
  if (!f.ciclo) return <Valor f={f} campo={CAMPO_DA_PLANILHA[coluna]} />;
  if (coluna === "usd") return f.moeda === "USD" && f.valor_ciclo_centavos ? <>{dolar(f.valor_ciclo_centavos)}</> : null;
  const v = coluna === "mensal" ? f.custo_mensal_centavos
    : coluna === "trimestral" || coluna === "semestral" ? (f.ciclo === coluna ? f.valor_ciclo_reais_centavos : null)
    : coluna === "anual" ? (f.ciclo === "pontual" ? null : f.custo_anual_centavos)
    : f.custo_anual_centavos;
  if (!v) {
    return f.moeda === "USD" && !f.cotacao_venda && coluna === "mensal"
      ? <span className="text-tinta-fraca italic" title="O valor em reais aparece quando a PTAX for gravada.">sem cotação</span> : null;
  }
  const conta = f.moeda === "USD" && f.valor_ciclo_centavos
    ? `${dolar(f.valor_ciclo_centavos)} × ${textoCotacao({ data: f.cotacao_data, venda: f.cotacao_venda, acrescimo: f.cotacao_acrescimo ?? "0" })}`
    : undefined;
  return <span title={conta}>{moeda(v)}</span>;
}

/** A periodicidade: o ciclo, ou o texto da planilha marcado para conferir. */
function Periodicidade({ f }: { f: Ferramenta }) {
  if (f.ciclo) return <>{f.periodicidade || TEXTO_CICLO[f.ciclo]}</>;
  return (
    <span title="Sem ciclo: abra a ferramenta e escolha a periodicidade.">
      {f.periodicidade && <span className="text-tinta-media italic">{f.periodicidade} </span>}
      {f.status !== "cancelada" && <span className="rounded bg-atencao-bg px-1 text-[10.5px] font-semibold text-atencao-tinta">conferir</span>}
    </span>
  );
}

function Url({ texto }: { texto: string | null }) {
  if (!texto) return null;
  const url = /^https?:\/\//i.test(texto) ? texto : /^[\w-]+(\.[\w-]+)+(\/\S*)?$/.test(texto) ? `https://${texto}` : null;
  if (!url) return <>{texto}</>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
      className="text-azul underline decoration-linha-forte underline-offset-2 hover:decoration-laranja">{texto}</a>
  );
}

// ── as colunas, na ordem da planilha ───────────────────────────────────────

type Col = { id: string; grupo: string; titulo: string; classe?: string; cel: (f: Ferramenta, c: Cofre) => ReactNode };
type Cofre = {
  permitido: boolean;
  revelado: (id: string) => { login: string | null; senha: string | null } | undefined;
  revelar: (id: string) => void;
  abrindo: string | null;
  /** O que o Conta Azul cobrou no último mês fechado (070): da ferramenta, ou do grupo dela. */
  cobrado: (f: Ferramenta) => ReactNode;
};

const TXT = "max-w-[240px] truncate";
const NUM = "num text-right whitespace-nowrap";
const conta = (f: Ferramenta) => (f.categoria_id
  ? `${f.categoria_codigo ? `${f.categoria_codigo} - ` : ""}${f.categoria_nome ?? ""}`
  : <span className="text-atencao-tinta">sem conta</span>);
const centro = (f: Ferramenta) => (f.centro_custo_id
  ? `${f.centro_custo_codigo} · ${f.centro_custo_nome}`
  : <span className="text-atencao-tinta">sem centro de custo</span>);

function colunas(aba: "controle_geral" | "cancelados"): Col[] {
  const contaCol: Col = { id: "conta", grupo: "CONTA AZUL", titulo: "Conta Contábil (CA)", classe: TXT, cel: conta };
  const ccCol: Col = { id: "cc", grupo: "CONTA AZUL", titulo: "Centro de Custo (CA)", classe: TXT, cel: centro };
  return [
    // A coluna da ferramenta é a presa à esquerda, desenhada à parte.
    { id: "tipo", grupo: "IDENTIFICAÇÃO", titulo: "Categoria / Tipo", classe: TXT, cel: (f) => f.tipo },
    { id: "familia", grupo: "IDENTIFICAÇÃO", titulo: "Família", classe: "whitespace-nowrap", cel: (f) => (f.familia ? ROTULO_FAMILIA[f.familia] : null) },
    { id: "moeda", grupo: "FINANCEIRO", titulo: "Moeda", cel: (f) => f.moeda },
    { id: "mensal", grupo: "FINANCEIRO", titulo: "R$ Mensal", classe: NUM, cel: (f) => <ValorDaColuna f={f} coluna="mensal" /> },
    { id: "trimestral", grupo: "FINANCEIRO", titulo: "R$ Trimestral", classe: NUM, cel: (f) => <ValorDaColuna f={f} coluna="trimestral" /> },
    { id: "semestral", grupo: "FINANCEIRO", titulo: "R$ Semestral", classe: NUM, cel: (f) => <ValorDaColuna f={f} coluna="semestral" /> },
    { id: "anual", grupo: "FINANCEIRO", titulo: "R$ Anual", classe: NUM, cel: (f) => <ValorDaColuna f={f} coluna="anual" /> },
    { id: "total", grupo: "FINANCEIRO", titulo: "Total (ano)", classe: NUM, cel: (f) => <ValorDaColuna f={f} coluna="total" /> },
    { id: "usd", grupo: "FINANCEIRO", titulo: "Valor USD (se aplic.)", classe: NUM, cel: (f) => <ValorDaColuna f={f} coluna="usd" /> },
    { id: "forma", grupo: "COBRANÇA", titulo: "Forma de Pagamento", classe: "whitespace-nowrap", cel: textoForma },
    { id: "periodicidade", grupo: "COBRANÇA", titulo: "Periodicidade", classe: TXT, cel: (f) => <Periodicidade f={f} /> },
    { id: "cobranca", grupo: "COBRANÇA", titulo: "Tipo de Cobrança", classe: TXT, cel: (f) => f.tipo_cobranca },
    { id: "dia", grupo: "COBRANÇA", titulo: "Dia Vencimento", classe: "whitespace-nowrap", cel: (f) => f.dia_vencimento },
    ...(aba === "cancelados" ? [ccCol, contaCol] : [contaCol, ccCol]),
    { id: "extrato", grupo: "CONTA AZUL", titulo: "Nome no Cartão / Extrato", classe: TXT, cel: (f) => f.nome_extrato },
    { id: "responsavel", grupo: "RESPONSABILIDADE", titulo: "Responsável Interno", classe: TXT, cel: (f) => f.responsavel },
    { id: "usuarios", grupo: "RESPONSABILIDADE", titulo: "Nº Usuários Ativos", classe: TXT, cel: (f) => f.usuarios_ativos },
    {
      id: "status", grupo: "RESPONSABILIDADE", titulo: "Status", classe: "whitespace-nowrap",
      cel: (f) => <span className={`rounded px-1.5 py-0.5 text-[11.5px] font-semibold ${COR_STATUS[f.status].celula}`}>{rotuloStatus(f)}</span>,
    },
    { id: "uso", grupo: "USABILIDADE", titulo: "Usabilidade Resumida", classe: TXT, cel: (f) => f.uso },
    { id: "login", grupo: "ACESSO", titulo: "Login / E-mail Acesso", classe: "whitespace-nowrap", cel: (f, c) => <CelulaCofre f={f} cofre={c} campo="login" /> },
    { id: "senha", grupo: "ACESSO", titulo: "Senha Acesso", classe: "whitespace-nowrap", cel: (f, c) => <CelulaCofre f={f} cofre={c} campo="senha" /> },
    { id: "url", grupo: "ACESSO", titulo: "URL de Acesso", classe: TXT, cel: (f) => <Url texto={f.url_acesso} /> },
    { id: "renovacao", grupo: "CONTROLE", titulo: "Renovação Automática", classe: "whitespace-nowrap text-center", cel: textoRenovacao },
    { id: "obs", grupo: "CONTROLE", titulo: "Observações", classe: "max-w-[360px] truncate", cel: (f) => f.observacao },
    { id: "cobrado", grupo: "CONTA AZUL (NEXA)", titulo: "Cobrado no mês fechado", classe: NUM, cel: (f, c) => c.cobrado(f) },
  ];
}

/** Para a busca: o texto de todas as colunas, menos as do cofre. */
const textoDaLinha = (f: Ferramenta) => [f.nome, f.tipo, f.periodicidade, f.tipo_cobranca, f.dia_vencimento, f.nome_extrato,
  f.responsavel, f.usuarios_ativos, f.uso, f.observacao, f.categoria_codigo, f.categoria_nome, f.centro_custo_nome,
  textoForma(f), rotuloStatus(f)].filter(Boolean).join(" ").toLowerCase();

function CelulaCofre({ f, cofre, campo }: { f: Ferramenta; cofre: Cofre; campo: "login" | "senha" }) {
  if (!cofre.permitido) return <span className="text-tinta-fraca" title="Só a administração vê">🔒</span>;
  if (!f.tem_acesso) return null;
  const r = cofre.revelado(f.id);
  if (r) {
    const v = r[campo];
    return v ? <Copiavel valor={v} /> : null;
  }
  if (campo === "senha") return <span className="text-tinta-fraca">••••••</span>;
  return (
    <button type="button" disabled={cofre.abrindo === f.id}
      onClick={(e) => { e.stopPropagation(); cofre.revelar(f.id); }}
      className="rounded border border-linha bg-superficie px-2 py-0.5 text-[11.5px] font-semibold text-tinta-media hover:border-laranja disabled:opacity-50"
      title="Mostra o login e a senha por um minuto. Fica registrado no Histórico.">
      {cofre.abrindo === f.id ? "Abrindo…" : "🔒 Ver acesso"}
    </button>
  );
}

function Copiavel({ valor }: { valor: string }) {
  const [copiado, setCopiado] = useState(false);
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="font-mono text-[12px]">{valor}</span>
      <button type="button" className="text-[11px] text-tinta-fraca hover:text-tinta"
        onClick={(e) => {
          e.stopPropagation();
          void navigator.clipboard?.writeText(valor).then(() => { setCopiado(true); setTimeout(() => setCopiado(false), 1500); });
        }}>
        {copiado ? "copiado" : "copiar"}
      </button>
    </span>
  );
}

// ── a página ───────────────────────────────────────────────────────────────

const ABAS = [
  { id: "controle", rotulo: "🗂 Controle Geral" },
  { id: "cancelados", rotulo: "Cancelados" },
  { id: "pendencias", rotulo: "⚠️ Pendências" },
  { id: "painel", rotulo: "💰 Painel Financeiro" },
] as const;
type AbaId = (typeof ABAS)[number]["id"];
const CHAVE_ABA = "nexa.ferramentas.aba";

export function Ferramentas() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["ferramentas"], queryFn: buscarFerramentas });
  const [aba, setAbaEstado] = useState<AbaId>(() => {
    try {
      const v = localStorage.getItem(CHAVE_ABA);
      return ABAS.some((a) => a.id === v) ? (v as AbaId) : "controle";
    } catch {
      return "controle";
    }
  });
  const setAba = (a: AbaId) => {
    setAbaEstado(a);
    try { localStorage.setItem(CHAVE_ABA, a); } catch { /* sem armazenamento: vale só nesta visita */ }
  };
  // `fornecedor`: o cadastro aberto de uma "cobrança sem ferramenta" (070). Salvo, o Nexa reconhece de novo.
  const [editando, setEditando] = useState<{ ferramenta: Ferramenta | null; candidata?: CandidataFerramenta; fornecedor?: string } | null>(null);

  // O cofre: o que foi revelado fica na tela por um minuto, e só na memória.
  const [revelados, setRevelados] = useState<Map<string, { login: string | null; senha: string | null }>>(new Map());
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  useEffect(() => () => { for (const t of timers.current.values()) clearTimeout(t); }, []);
  const revelar = useMutation({
    mutationFn: (id: string) => revelarAcessoFerramenta(id).then((r) => ({ id, r })),
    onSuccess: ({ id, r }) => {
      setRevelados((m) => new Map(m).set(id, r));
      clearTimeout(timers.current.get(id));
      timers.current.set(id, setTimeout(() => setRevelados((m) => { const n = new Map(m); n.delete(id); return n; }), 60_000));
    },
  });
  const dados = q.data;
  // O cobrado no último mês fechado (070): o da ferramenta, quando a cobrança é
  // dela; senão, avisa que o fornecedor cobrou o grupo inteiro.
  const cobrado = useMemo(() => {
    const mes = mesFechado(new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo" }).format(new Date()));
    const c = dados?.cobrancas;
    const daFerramenta = new Map<string, bigint>();
    for (const x of c?.porFerramenta ?? []) {
      if (x.mes === mes) daFerramenta.set(x.ferramenta_id, (daFerramenta.get(x.ferramenta_id) ?? 0n) + BigInt(x.real_centavos) + BigInt(x.iof_centavos));
    }
    const grupoDe = new Map<string, string>();
    for (const g of c?.grupos ?? []) if (g.ferramentas.length > 1) for (const id of g.ferramentas) if (!grupoDe.has(id)) grupoDe.set(id, g.chave);
    const cobrados = new Set((c?.meses ?? []).filter((m) => m.mes === mes && BigInt(m.real_centavos) > 0n).map((m) => m.chave));
    return (f: Ferramenta): ReactNode => {
      const v = daFerramenta.get(f.id);
      if (v !== undefined) return moeda(v.toString());
      const g = grupoDe.get(f.id);
      return g && cobrados.has(g)
        ? <span className="text-tinta-fraca italic" title="O fornecedor cobrou o grupo inteiro; o Painel Financeiro compara o grupo.">no grupo {g.toUpperCase()}</span>
        : null;
    };
  }, [dados]);
  const cofre: Cofre = {
    permitido: !!dados?.cofre.permitido,
    revelado: (id) => revelados.get(id),
    revelar: (id) => revelar.mutate(id),
    abrindo: revelar.isPending ? (revelar.variables ?? null) : null,
    cobrado,
  };

  const contagem = useMemo(() => {
    const fs = dados?.ferramentas ?? [];
    return {
      controle: fs.filter((f) => f.aba === "controle_geral").length,
      cancelados: fs.filter((f) => f.aba === "cancelados").length,
      pendencias: (dados?.pendencias ?? []).filter((p) => !p.resolvido).length,
    } as Partial<Record<AbaId, number>>;
  }, [dados]);

  const salvo = async () => {
    const doFornecedor = !!editando?.fornecedor;
    setEditando(null);
    // A ferramenta nova casa com as cobranças do fornecedor, e a pendência dele fecha.
    if (doFornecedor) await reconhecerCobrancas().catch(() => undefined);
    void qc.invalidateQueries({ queryKey: ["ferramentas"] });
  };
  // Excluída ou restaurada (078): as cobranças que eram dela voltam a ser
  // reconhecidas entre as que ficaram, antes de a tela recarregar.
  const recarregarComCobrancas = async () => {
    setEditando(null);
    await reconhecerCobrancas().catch(() => undefined);
    void qc.invalidateQueries({ queryKey: ["ferramentas"] });
  };

  return (
    <div className="pagina pb-20">
      <header className="pt-7 pb-4">
        <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">Ferramentas</p>
        <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">Controle de Ferramentas</h1>
        <p className="mt-1 text-[14px] text-tinta-fraca">
          A planilha do Financeiro, com as mesmas colunas. Clique numa linha para editar.
        </p>
      </header>

      <nav role="tablist" aria-label="Abas da planilha" className="thin-scroll mb-4 flex gap-1 overflow-x-auto overflow-y-hidden border-b border-linha">
        {ABAS.map((a) => (
          <button key={a.id} type="button" role="tab" aria-selected={aba === a.id} onClick={() => setAba(a.id)}
            className={`-mb-px shrink-0 rounded-t-lg border px-3 py-2 text-[13px] font-semibold whitespace-nowrap ${
              aba === a.id ? "border-linha border-b-superficie bg-superficie text-tinta" : "border-transparent text-tinta-fraca hover:text-tinta"
            }`}>
            {a.rotulo}
            {contagem[a.id] !== undefined && <span className="num ml-1.5 text-[11.5px] font-normal text-tinta-fraca">{contagem[a.id]}</span>}
          </button>
        ))}
      </nav>

      {q.isPending && <p className="text-[13px] text-tinta-fraca">Carregando…</p>}
      {q.isError && <Vazio titulo="Não foi possível carregar as ferramentas" dica={(q.error as FalhaApi).message} />}
      {revelar.isError && (
        <p className="mb-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">{(revelar.error as FalhaApi).message}</p>
      )}
      {dados?.cofre.permitido && !dados.cofre.configurado && (
        <p className="mb-3 rounded-lg border border-atencao-linha bg-atencao-bg px-3 py-2 text-[12.5px] text-atencao-tinta">
          O cofre de senhas não está configurado neste ambiente (falta a variável COFRE_CHAVE): login e senha não abrem nem gravam.
        </p>
      )}

      {dados && (aba === "controle" || aba === "cancelados") && (
        <AbaGrade
          aba={aba === "controle" ? "controle_geral" : "cancelados"}
          dados={dados}
          cofre={cofre}
          onEditar={(f) => setEditando({ ferramenta: f })}
          onNova={() => setEditando({ ferramenta: null })}
        />
      )}
      {dados && aba === "cancelados" && <Excluidas itens={dados.excluidas ?? []} onRestaurado={recarregarComCobrancas} />}
      {dados && aba === "pendencias" && (
        <AbaPendencias dados={dados}
          onCadastrar={(c) => setEditando({ ferramenta: null, candidata: c })}
          onCadastrarFornecedor={(chave) => setEditando({ ferramenta: null, fornecedor: chave })}
          onEditar={(f) => setEditando({ ferramenta: f })} />
      )}
      {dados && aba === "painel" && <AbaPainel dados={dados} onEditar={(f) => setEditando({ ferramenta: f })} />}

      {editando && (
        <ModalFerramenta
          ferramenta={editando.ferramenta}
          candidata={editando.candidata}
          fornecedor={editando.fornecedor}
          cofre={dados?.cofre ?? { permitido: false, configurado: false }}
          cotacao={dados?.cotacao ?? null}
          onFechar={() => setEditando(null)}
          onSalvo={salvo}
          onExcluido={recarregarComCobrancas}
        />
      )}
    </div>
  );
}

// ── Controle Geral e Cancelados ────────────────────────────────────────────

function AbaGrade({ aba, dados, cofre, onEditar, onNova }: {
  aba: "controle_geral" | "cancelados";
  dados: DadosFerramentas;
  cofre: Cofre;
  onEditar: (f: Ferramenta) => void;
  onNova: () => void;
}) {
  const [busca, setBusca] = useState("");
  const [cc, setCc] = useState("todos");
  const [cartao, setCartao] = useState("todos");
  const [status, setStatus] = useState("todos");
  const nome = useLarguraColuna(`ferramentas.${aba}`, 240);
  // Para a marca do término previsto (079): o dia de hoje em São Paulo.
  const hoje = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo" }).format(new Date());

  const daAba = dados.ferramentas.filter((f) => f.aba === aba);
  const centros = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of daAba) if (f.centro_custo_id) m.set(f.centro_custo_id, `${f.centro_custo_codigo} · ${f.centro_custo_nome}`);
    return [...m].sort((a, b) => a[1].localeCompare(b[1], undefined, { numeric: true }));
  }, [daAba]);
  const cartoes = [...new Set(daAba.map((f) => f.cartao_final).filter((x): x is string => !!x))].sort();
  const termo = busca.trim().toLowerCase();
  const visiveis = daAba.filter((f) =>
    (cc === "todos" || (cc === "sem" ? !f.centro_custo_id : f.centro_custo_id === cc))
    && (cartao === "todos" || f.cartao_final === cartao)
    && (status === "todos" || (status === "conferir" ? f.avisos_importacao.length > 0 : f.status === status))
    && (!termo || textoDaLinha(f).includes(termo)));

  const cols = colunas(aba);
  const grupos: { nome: string; span: number }[] = [{ nome: "IDENTIFICAÇÃO", span: 1 }];
  for (const c of cols) {
    const ultimo = grupos[grupos.length - 1]!;
    if (ultimo.nome === c.grupo) ultimo.span++; else grupos.push({ nome: c.grupo, span: 1 });
  }

  return (
    <>
      <FiltrosRecolhiveis
        chave={`ferramentas.${aba}`}
        resumo={[cc !== "todos" ? (cc === "sem" ? "sem centro de custo" : centros.find(([id]) => id === cc)?.[1]) : null,
          cartao !== "todos" ? `cartão final ${cartao}` : null,
          status !== "todos" ? (status === "conferir" ? "para conferir" : ROTULO_STATUS_FERRAMENTA[status as Ferramenta["status"]]) : null,
          termo ? `“${busca.trim()}”` : null].filter(Boolean).join(" · ") || "Todas"}
        acoes={aba === "controle_geral" ? (
          <button type="button" onClick={onNova} className="rounded-lg bg-laranja px-3 py-1.5 text-[12.5px] font-bold text-azul">
            + Nova ferramenta
          </button>
        ) : undefined}
      >
        <CampoCompacto rotulo="Buscar">
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Em qualquer coluna"
            className="w-[220px] rounded-lg border border-linha bg-superficie px-2.5 py-1.5 text-[12.5px]" />
        </CampoCompacto>
        {aba === "controle_geral" && (
          <CampoCompacto rotulo="Status">
            <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status"
              className="rounded-lg border border-linha bg-superficie px-2.5 py-1.5 text-[12.5px]">
              <option value="todos">Todos</option>
              <option value="ativa">Ativo</option>
              <option value="verificar">Verificar</option>
              <option value="nova">NOVO-Validar</option>
              <option value="conferir">Para conferir (importação)</option>
            </select>
          </CampoCompacto>
        )}
        <CampoCompacto rotulo="Centro de custo">
          <select value={cc} onChange={(e) => setCc(e.target.value)} aria-label="Centro de custo"
            className="max-w-[220px] rounded-lg border border-linha bg-superficie px-2.5 py-1.5 text-[12.5px]">
            <option value="todos">Todos</option>
            <option value="sem">Sem centro de custo</option>
            {centros.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
          </select>
        </CampoCompacto>
        <CampoCompacto rotulo="Cartão">
          <select value={cartao} onChange={(e) => setCartao(e.target.value)} aria-label="Cartão"
            className="rounded-lg border border-linha bg-superficie px-2.5 py-1.5 text-[12.5px]">
            <option value="todos">Todos</option>
            {cartoes.map((c) => <option key={c} value={c}>Final {c}</option>)}
          </select>
        </CampoCompacto>
      </FiltrosRecolhiveis>

      {daAba.length === 0 ? (
        <Painel>
          <Vazio titulo={aba === "cancelados" ? "Nenhuma ferramenta cancelada" : "Nenhuma ferramenta cadastrada ainda"}
            dica={aba === "cancelados" ? "Ao cancelar uma ferramenta, ela passa para esta aba." : "Traga a planilha pela aba Importar, ou cadastre uma a uma em “+ Nova ferramenta”."} />
        </Painel>
      ) : visiveis.length === 0 ? (
        <Painel><Vazio titulo="Nada com esse filtro" dica="Limpe a busca ou troque o filtro." /></Painel>
      ) : (
        <CaixaGrade>
          <table className="border-separate border-spacing-0 text-[12.5px]">
            <thead className="sticky top-0 z-20 bg-superficie-2 text-left text-[11px] font-semibold tracking-wide text-tinta-fraca">
              <tr>
                {grupos.map((g, i) => (
                  <th key={`${g.nome}-${i}`} colSpan={g.span}
                    className={`border-b border-linha bg-azul px-3 py-1.5 text-center text-[10.5px] tracking-[0.12em] text-white uppercase ${i === 0 ? "sticky left-0 z-30" : ""}`}>
                    {i === 0 ? (
                      <NomeNaLargura largura={nome.largura} className="justify-center">{g.nome}</NomeNaLargura>
                    ) : g.nome}
                  </th>
                ))}
              </tr>
              <tr>
                <th className="sticky left-0 z-30 border-b border-r border-linha bg-superficie-2 px-3 py-2">
                  <NomeNaLargura largura={nome.largura}>Ferramenta (Nome Fantasia)</NomeNaLargura>
                  <AlcaLargura coluna={nome} />
                </th>
                {cols.map((c) => (
                  <th key={c.id} className={`border-b border-linha px-3 py-2 whitespace-nowrap ${c.classe?.includes("text-right") ? "text-right" : ""}`}>
                    {c.titulo}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visiveis.map((f) => {
                const cor = COR_STATUS[f.status].linha;
                return (
                  <tr key={f.id} onClick={() => onEditar(f)} className={`group cursor-pointer ${cor}`}>
                    <td className={`sticky left-0 z-10 border-b border-r border-linha px-3 py-1.5 ${cor || "bg-superficie"} group-hover:bg-superficie-2`}>
                      <NomeNaLargura largura={nome.largura}>
                        <span className="truncate font-semibold text-tinta" title={f.nome}>{f.nome}</span>
                        {f.avisos_importacao.length > 0 && (
                          <span className="shrink-0 text-[11px] text-atencao-tinta" title={`Para conferir: ${f.avisos_importacao.join("; ")}`}>⚠</span>
                        )}
                        {f.teste_ate && f.status !== "cancelada" && (
                          <span title="Término previsto"
                            className={`shrink-0 rounded-full border px-1.5 py-px text-[10px] font-semibold whitespace-nowrap ${
                              f.teste_ate < hoje ? "border-risco-linha bg-risco-bg text-risco-tinta" : "border-neutro-linha bg-neutro-bg text-neutro-tinta"}`}>
                            {f.teste_ate < hoje ? "terminou" : "termina"} {dataCurta(f.teste_ate)}
                          </span>
                        )}
                      </NomeNaLargura>
                    </td>
                    {cols.map((c) => {
                      const v = c.cel(f, cofre);
                      return (
                        <td key={c.id} className={`border-b border-linha px-3 py-1.5 text-tinta-media group-hover:bg-superficie-2/60 ${c.classe ?? ""}`}
                          title={typeof v === "string" ? v : undefined}>
                          {v}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CaixaGrade>
      )}
      <p className="mt-2 text-[11.5px] text-tinta-fraca">
        {visiveis.length} de {daAba.length} ferramenta(s).
        {aba === "cancelados" && " As canceladas do meio do Controle Geral da planilha também aparecem aqui."}
        {!cofre.permitido && " 🔒 Login e senha: só a administração vê."}
      </p>
    </>
  );
}

// ── as excluídas (078), no fim da aba Cancelados ─────────────────────────────

function Excluidas({ itens, onRestaurado }: { itens: FerramentaExcluida[]; onRestaurado: () => void }) {
  const [aberta, setAberta] = useState(false);
  const restaurar = useMutation({ mutationFn: restaurarFerramenta, onSuccess: onRestaurado });
  if (itens.length === 0) return null;
  return (
    <Painel className="mt-6">
      <button type="button" onClick={() => setAberta((a) => !a)} aria-expanded={aberta}
        className="flex w-full flex-wrap items-center gap-2 text-left text-[14px] font-semibold">
        <span aria-hidden className={`text-[11px] text-tinta-fraca transition-transform ${aberta ? "" : "-rotate-90"}`}>▾</span>
        Excluídas <span className="num text-[12px] font-normal text-tinta-fraca">{itens.length}</span>
        <span className="text-[12px] font-normal text-tinta-fraca">— linhas que não deveriam existir; não entram em tela nem conta</span>
      </button>
      {aberta && (
        <>
          {restaurar.isError && <p className="mt-3 text-[12.5px] text-risco-tinta">{(restaurar.error as FalhaApi).message}</p>}
          <ul className="mt-3">
            {itens.map((e) => (
              <li key={e.id} className="flex flex-wrap items-start justify-between gap-3 border-t border-linha py-2.5 text-[13px]">
                <div className="min-w-0">
                  <p className="font-medium">{e.nome}</p>
                  <p className="text-[12px] text-tinta-fraca">
                    {new Date(e.excluida_em).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", dateStyle: "short", timeStyle: "short" })}
                    {e.excluida_por_nome ? ` · ${e.excluida_por_nome}` : ""} · {e.origem === "planilha" ? "veio da planilha" : "cadastrada no Nexa"}
                  </p>
                  <p className="mt-0.5 text-[12.5px] text-tinta-media">“{e.motivo}”</p>
                </div>
                <button type="button" disabled={restaurar.isPending} onClick={() => restaurar.mutate(e.id)}
                  className={CLASSE_BOTAO_BARRA}>
                  {restaurar.isPending && restaurar.variables === e.id ? "Restaurando…" : "Restaurar"}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </Painel>
  );
}

// ── o formulário, na ordem das colunas ─────────────────────────────────────

function Grupo({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <fieldset className="grid gap-3 border-t border-linha pt-3 sm:grid-cols-2">
      <legend className="pr-2 text-[10.5px] font-bold tracking-[0.12em] text-tinta-fraca uppercase">{titulo}</legend>
      {children}
    </fieldset>
  );
}

const VAZIA: FerramentaEdicao = {
  nome: "", tipo: null, status: "ativa", moeda: "BRL", valor_mensal_centavos: null, valor_trimestral_centavos: null,
  valor_semestral_centavos: null, valor_anual_centavos: null, valor_total_ano_centavos: null, valor_usd_centavos: null,
  periodicidade: null, ciclo: null, valor_ciclo_centavos: null, familia: null, tipo_cobranca: "Assinatura", forma_pagamento: "cartao", cartao_final: null, dia_vencimento: null,
  dia_cobranca: null, categoria_id: null, centro_custo_id: null, nome_extrato: null, termos_busca: [], responsavel: null,
  usuarios_ativos: null, qtd_usuarios: null, uso: null, url_acesso: null, renovacao: "automatica", proxima_renovacao: null,
  teste: false, teste_ate: null, cancelada_em: null, observacao: null, tendencia: null, texto_planilha: {}, avisos_importacao: [],
};

type Cadastros = {
  centros: { id: string; codigo: string; nome: string }[];
  categorias: { id: string; codigo: string | null; nome: string; natureza: string }[];
};

function ModalFerramenta({ ferramenta, candidata, fornecedor, cofre, cotacao, onFechar, onSalvo, onExcluido }: {
  ferramenta: Ferramenta | null;
  candidata?: CandidataFerramenta;
  fornecedor?: string;
  cofre: DadosFerramentas["cofre"];
  cotacao: Cotacao;
  onFechar: () => void;
  onSalvo: () => void;
  onExcluido: () => void;
}) {
  // Excluir (078): pede confirmação e motivo, no próprio modal.
  const [excluindo, setExcluindo] = useState(false);
  const [motivoExclusao, setMotivoExclusao] = useState("");
  const excluir = useMutation({
    mutationFn: () => excluirFerramenta(ferramenta!.id, motivoExclusao.trim()),
    onSuccess: onExcluido,
  });
  const inicial = useMemo<FerramentaEdicao>(() => {
    if (ferramenta) {
      const { origem: _o, origem_ref: _r, linha_planilha: _l, centro_custo_codigo: _a, centro_custo_nome: _b,
        categoria_codigo: _c, categoria_nome: _d, custo_mensal_centavos: _e, aba: _f, tem_acesso: _g, ciclo_fonte: _h,
        valor_ciclo_reais_centavos: _i, meses_ciclo: _j, cotacao_data: _k, cotacao_venda: _p, cotacao_acrescimo: _m,
        custo_anual_centavos: _n, ...resto } = ferramenta;
      return { ...resto, texto_planilha: resto.texto_planilha ?? {}, avisos_importacao: resto.avisos_importacao ?? [] };
    }
    if (candidata) {
      return { ...VAZIA, nome: candidata.ferramenta_texto, tipo: candidata.classificacao, status: "nova",
        nome_extrato: candidata.descritivo_extrato, candidata_id: candidata.id };
    }
    if (fornecedor) {
      // O nome no extrato é o próprio fornecedor: é por ele que as cobranças vão casar.
      return { ...VAZIA, nome: fornecedor.charAt(0).toUpperCase() + fornecedor.slice(1), status: "nova", nome_extrato: fornecedor.toUpperCase() };
    }
    return VAZIA;
  }, [ferramenta, candidata, fornecedor]);
  const [f, setF] = useState<FerramentaEdicao>(inicial);
  const cad = useQuery({ queryKey: ["cadastros"], queryFn: () => api.get<Cadastros>("/api/cadastros") });
  const set = <K extends keyof FerramentaEdicao>(k: K, v: FerramentaEdicao[K]) => setF((x) => ({ ...x, [k]: v }));
  const txt = (v: string) => (v.trim() ? v : null);

  // O acesso: só para quem vê o cofre, e só depois de abrir (cada abertura vai para o Histórico).
  const [acesso, setAcesso] = useState<{ login: string; senha: string } | null>(null);
  const [acessoOriginal, setAcessoOriginal] = useState<{ login: string; senha: string } | null>(null);
  const [verSenha, setVerSenha] = useState(false);
  const abrirAcesso = useMutation({
    mutationFn: () => revelarAcessoFerramenta(ferramenta!.id),
    onSuccess: (r) => { const a = { login: r.login ?? "", senha: r.senha ?? "" }; setAcesso(a); setAcessoOriginal(a); },
  });

  // O ciclo libera o campo de valor (067). Na ferramenta que ainda não tinha
  // ciclo, o valor vem da coluna da planilha que corresponde a ele, para a
  // pessoa conferir em vez de redigitar.
  const escolherCiclo = (c: Ciclo | null) => setF((x) => ({
    ...x,
    ciclo: c,
    valor_ciclo_centavos: c && !x.ciclo && !x.valor_ciclo_centavos ? valorDaPlanilha(c, x.moeda, x) : x.valor_ciclo_centavos,
  }));
  const calculo = calcularCiclo(f.ciclo, f.moeda, f.valor_ciclo_centavos, cotacao);
  const infoCiclo = CICLOS.find((c) => c.valor === f.ciclo);
  const simbolo = f.moeda === "USD" ? "US$" : "R$";
  const planilhaValores = ([
    ["mensal", f.valor_mensal_centavos], ["trimestral", f.valor_trimestral_centavos],
    ["semestral", f.valor_semestral_centavos], ["anual", f.valor_anual_centavos],
    ["total do ano", f.valor_total_ano_centavos], ["em dólar", f.valor_usd_centavos],
  ] as const).filter(([, v]) => v);

  const salvar = useMutation({
    mutationFn: async () => {
      // O texto da planilha de uma coluna que a pessoa preencheu deixa de valer.
      const texto_planilha = { ...(f.texto_planilha ?? {}) };
      for (const k of Object.keys(texto_planilha) as (keyof FerramentaEdicao)[]) {
        if (f[k] !== inicial[k]) delete texto_planilha[k as string];
      }
      // A periodicidade escrita é a do ciclo; sem ciclo, fica a da planilha.
      const periodicidade = f.ciclo ? TEXTO_CICLO[f.ciclo] : f.periodicidade;
      const derivados = derivarCampos({ ...f, periodicidade });
      const r = await salvarFerramenta({
        ...f,
        periodicidade,
        valor_ciclo_centavos: f.ciclo ? f.valor_ciclo_centavos : null,
        nome: f.nome.trim(),
        cartao_final: f.forma_pagamento === "cartao" ? f.cartao_final : null,
        cancelada_em: f.status === "cancelada" ? f.cancelada_em : null,
        ...derivados,
        // Data de renovação digitada à mão vale mais que a tirada do texto.
        proxima_renovacao: f.proxima_renovacao !== inicial.proxima_renovacao ? f.proxima_renovacao : (derivados.proxima_renovacao ?? f.proxima_renovacao),
        texto_planilha,
      });
      if (acesso && cofre.permitido && (acesso.login !== acessoOriginal?.login || acesso.senha !== acessoOriginal?.senha)) {
        await salvarAcessoFerramenta(r.id, {
          ...(acesso.login !== acessoOriginal?.login ? { login: acesso.login || null } : {}),
          ...(acesso.senha !== acessoOriginal?.senha ? { senha: acesso.senha || null } : {}),
        });
      }
      return r;
    },
    onSuccess: onSalvo,
  });
  const cartaoInvalido = f.forma_pagamento === "cartao" && !!f.cartao_final && !/^\d{4}$/.test(f.cartao_final);

  const campo = "mt-1 w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[13.5px]";
  const rot = "text-[12px] font-semibold text-tinta-media";
  const naPlanilha = (k: string) => f.texto_planilha?.[k]
    ? <span className="mt-1 block text-[11.5px] text-tinta-fraca">Na planilha: “{f.texto_planilha[k]}”</span> : null;
  return (
    <Modal titulo={ferramenta ? ferramenta.nome : candidata ? `Cadastrar: ${candidata.ferramenta_texto}`
      : fornecedor ? `Cadastrar: ${fornecedor.toUpperCase()}` : "Nova ferramenta"} onFechar={onFechar} largura="max-w-3xl">
      {ferramenta?.origem === "planilha" && (
        <p className="mb-3 text-[12px] text-tinta-fraca">
          Veio da planilha do Financeiro (linha {ferramenta.linha_planilha}). Depois de editada aqui, uma nova importação não a sobrescreve.
        </p>
      )}
      {f.avisos_importacao.length > 0 && (
        <div className="mb-3 rounded-lg border border-atencao-linha bg-atencao-bg px-3 py-2 text-[12.5px] text-atencao-tinta">
          <p className="font-semibold">Para conferir</p>
          <ul className="mt-1 list-disc pl-5">{f.avisos_importacao.map((a) => <li key={a}>{a}</li>)}</ul>
          <button type="button" className="mt-1.5 text-[12px] font-semibold underline" onClick={() => set("avisos_importacao", [])}>
            Marcar como conferido
          </button>
        </div>
      )}
      <div className="grid gap-4">
        <Grupo titulo="Identificação">
          <label className="block"><span className={rot}>Ferramenta (Nome Fantasia) *</span>
            <input value={f.nome} onChange={(e) => set("nome", e.target.value)} className={campo} /></label>
          <label className="block"><span className={rot}>Categoria / Tipo</span>
            <input value={f.tipo ?? ""} onChange={(e) => set("tipo", txt(e.target.value))} className={campo} /></label>
          <label className="block"><span className={rot}>Família</span>
            <select value={f.familia ?? ""} onChange={(e) => set("familia", (e.target.value || null) as Familia | null)} className={campo}>
              <option value="">Sugerir pelo tipo</option>
              {FAMILIAS.map((x) => <option key={x.valor} value={x.valor}>{x.rotulo}</option>)}
            </select></label>
        </Grupo>

        <Grupo titulo="Financeiro">
          <label className="block"><span className={rot}>Periodicidade da cobrança *</span>
            <select value={f.ciclo ?? ""} onChange={(e) => escolherCiclo((e.target.value || null) as Ciclo | null)} className={campo}>
              <option value="">Escolha…</option>
              {CICLOS.map((c) => <option key={c.valor} value={c.valor}>{c.rotulo}</option>)}
            </select>
            {!f.ciclo && f.periodicidade && (
              <span className="mt-1 block text-[11.5px] text-atencao-tinta">Na planilha: “{f.periodicidade}”. Escolha o ciclo que vale.</span>
            )}</label>
          <label className="block"><span className={rot}>Moeda</span>
            <select value={f.moeda} onChange={(e) => set("moeda", e.target.value as "BRL" | "USD")} className={campo}>
              <option value="BRL">Real (R$)</option><option value="USD">Dólar (US$)</option>
            </select></label>
          <label className="block"><span className={rot}>{infoCiclo ? `${infoCiclo.campo} (${simbolo})` : "Valor"}</span>
            <div className="mt-1"><CampoDinheiro valorCentavos={f.ciclo ? f.valor_ciclo_centavos : null} prefixo={simbolo}
              disabled={!f.ciclo} placeholder={f.ciclo ? "0,00" : "escolha a periodicidade"}
              onMudar={(c) => set("valor_ciclo_centavos", c)} /></div></label>
          <div className="self-end rounded-lg bg-superficie-2 px-3 py-2 text-[12.5px] leading-relaxed">
            {!f.ciclo ? (
              <span className="text-tinta-fraca">O mensal, o anual e o real saem do valor do ciclo.</span>
            ) : calculo.reaisCiclo === null ? (
              <span className="text-tinta-fraca">
                {f.moeda === "USD" && !cotacao?.venda ? "Sem cotação do dólar ainda: o real aparece quando a PTAX for gravada." : "Informe o valor."}
              </span>
            ) : (
              <>
                {f.moeda === "USD" && (
                  <p><span className="text-tinta-fraca">Em reais:</span> <b className="num">{moeda(calculo.reaisCiclo.toString())}</b>
                    <span className="block text-[11px] text-tinta-fraca">{textoCotacao(cotacao)}</span></p>
                )}
                {calculo.mensal !== null && <p><span className="text-tinta-fraca">Mensal equivalente:</span> <b className="num">{moeda(calculo.mensal.toString())}</b></p>}
                {calculo.anual !== null && (
                  <p><span className="text-tinta-fraca">{f.ciclo === "pontual" ? "No ano (a compra):" : "No ano:"}</span> <b className="num">{moeda(calculo.anual.toString())}</b></p>
                )}
              </>
            )}
          </div>
          {f.texto_planilha?.valor_mensal_centavos || planilhaValores.length > 0 ? (
            <p className="text-[11.5px] text-tinta-fraca sm:col-span-2">
              Na planilha: {planilhaValores.map(([r, v]) => `${r} ${r === "em dólar" ? dolar(v) : moeda(v)}`).join(" · ")}
              {f.texto_planilha?.valor_mensal_centavos ? ` · “${f.texto_planilha.valor_mensal_centavos}”` : ""}
            </p>
          ) : null}
        </Grupo>

        <Grupo titulo="Cobrança">
          <label className="block"><span className={rot}>Forma de Pagamento</span>
            <select value={f.forma_pagamento ?? ""} onChange={(e) => set("forma_pagamento", (e.target.value || null) as Ferramenta["forma_pagamento"])} className={campo}>
              <option value="">—</option>
              {Object.entries(ROTULO_FORMA).map(([v, r]) => <option key={v} value={v}>{r}</option>)}
            </select>{naPlanilha("forma_pagamento")}</label>
          {f.forma_pagamento === "cartao" && (
            <label className="block"><span className={rot}>Final do cartão (4 dígitos)</span>
              <input value={f.cartao_final ?? ""} inputMode="numeric" maxLength={4}
                onChange={(e) => set("cartao_final", txt(e.target.value.replace(/\D/g, "")))}
                aria-invalid={cartaoInvalido} className={`${campo} ${cartaoInvalido ? "border-risco-linha" : ""}`} /></label>
          )}
          <label className="block"><span className={rot}>Tipo de Cobrança</span>
            <input value={f.tipo_cobranca ?? ""} onChange={(e) => set("tipo_cobranca", txt(e.target.value))} list="ferr-cobranca" className={campo} />
            <datalist id="ferr-cobranca">{["Assinatura", "Add-on mensal", "Por uso", "Por seat adicional", "Compra única", "Parcelado"].map((v) => <option key={v} value={v} />)}</datalist></label>
          <label className="block"><span className={rot}>Dia Vencimento</span>
            <input value={f.dia_vencimento ?? ""} onChange={(e) => set("dia_vencimento", txt(e.target.value))} placeholder="Ex.: 5, Variado, 20/01/2027" className={campo} /></label>
          <label className="block"><span className={rot}>Próxima renovação</span>
            <input type="date" value={f.proxima_renovacao ?? ""} onChange={(e) => set("proxima_renovacao", txt(e.target.value))} className={campo} /></label>
        </Grupo>

        <Grupo titulo="Conta Azul">
          <label className="block"><span className={rot}>Conta Contábil (CA)</span>
            <select value={f.categoria_id ?? ""} onChange={(e) => set("categoria_id", e.target.value || null)} className={campo}>
              <option value="">—</option>
              {(cad.data?.categorias ?? []).filter((c) => c.natureza !== "receita")
                .map((c) => <option key={c.id} value={c.id}>{c.codigo ? `${c.codigo} - ` : ""}{c.nome}</option>)}
            </select></label>
          <label className="block"><span className={rot}>Centro de Custo (CA)</span>
            <select value={f.centro_custo_id ?? ""} onChange={(e) => set("centro_custo_id", e.target.value || null)} className={campo}>
              <option value="">—</option>
              {(cad.data?.centros ?? []).map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nome}</option>)}
            </select></label>
          <label className="block sm:col-span-2"><span className={rot}>Nome no Cartão / Extrato</span>
            <input value={f.nome_extrato ?? ""} onChange={(e) => set("nome_extrato", txt(e.target.value))} placeholder="Ex.: EBN CANVA / CANVA" className={campo} />
            <span className="mt-1 block text-[11.5px] text-tinta-fraca">
              É por esses nomes, separados por “/”, que o Nexa vai reconhecer as cobranças: {derivarCampos(f).termos_busca.join(" · ") || "nenhum ainda"}.
            </span></label>
        </Grupo>

        <Grupo titulo="Responsabilidade">
          <label className="block"><span className={rot}>Responsável Interno</span>
            <input value={f.responsavel ?? ""} onChange={(e) => set("responsavel", txt(e.target.value))} className={campo} /></label>
          <label className="block"><span className={rot}>Nº Usuários Ativos</span>
            <input value={f.usuarios_ativos ?? ""} onChange={(e) => set("usuarios_ativos", txt(e.target.value))} placeholder="Um número, ou quem usa" className={campo} /></label>
          <label className="block"><span className={rot}>Status</span>
            <select value={f.status} onChange={(e) => set("status", e.target.value as Ferramenta["status"])} className={campo}>
              {Object.entries(ROTULO_STATUS_FERRAMENTA).map(([v, r]) => <option key={v} value={v}>{r}</option>)}
            </select>{naPlanilha("status")}</label>
          {f.status === "cancelada" && (
            <label className="block"><span className={rot}>Cancelado em</span>
              <input type="date" value={f.cancelada_em ?? ""} onChange={(e) => set("cancelada_em", txt(e.target.value))} className={campo} /></label>
          )}
        </Grupo>

        <Grupo titulo="Usabilidade">
          <label className="block sm:col-span-2"><span className={rot}>Usabilidade Resumida</span>
            <input value={f.uso ?? ""} onChange={(e) => set("uso", txt(e.target.value))} className={campo} /></label>
        </Grupo>

        <Grupo titulo="Acesso">
          {!cofre.permitido ? (
            <p className="text-[12.5px] text-tinta-fraca sm:col-span-2">🔒 Login e senha: só a administração vê e altera.</p>
          ) : !cofre.configurado ? (
            <p className="text-[12.5px] text-atencao-tinta sm:col-span-2">O cofre não está configurado neste ambiente (COFRE_CHAVE).</p>
          ) : ferramenta?.tem_acesso && !acesso ? (
            <div className="sm:col-span-2">
              <button type="button" disabled={abrirAcesso.isPending} onClick={() => abrirAcesso.mutate()} className={CLASSE_BOTAO_BARRA}>
                {abrirAcesso.isPending ? "Abrindo…" : "🔒 Ver e editar login e senha"}
              </button>
              <span className="ml-2 text-[11.5px] text-tinta-fraca">Fica registrado no Histórico.</span>
              {abrirAcesso.isError && <p className="mt-1 text-[12px] text-risco-tinta">{(abrirAcesso.error as FalhaApi).message}</p>}
            </div>
          ) : (
            <>
              <label className="block"><span className={rot}>Login / E-mail Acesso</span>
                <input value={acesso?.login ?? ""} autoComplete="off" onChange={(e) => setAcesso((a) => ({ login: e.target.value, senha: a?.senha ?? "" }))} className={campo} /></label>
              <label className="block"><span className={rot}>Senha Acesso</span>
                <div className="flex gap-2">
                  <input type={verSenha ? "text" : "password"} value={acesso?.senha ?? ""} autoComplete="new-password"
                    onChange={(e) => setAcesso((a) => ({ login: a?.login ?? "", senha: e.target.value }))} className={campo} />
                  <button type="button" onClick={() => setVerSenha((v) => !v)} className="mt-1 shrink-0 text-[12px] text-tinta-fraca hover:text-tinta">
                    {verSenha ? "ocultar" : "mostrar"}
                  </button>
                </div></label>
            </>
          )}
          <label className="block sm:col-span-2"><span className={rot}>URL de Acesso</span>
            <input value={f.url_acesso ?? ""} onChange={(e) => set("url_acesso", txt(e.target.value))} placeholder="https://…" className={campo} /></label>
        </Grupo>

        <Grupo titulo="Controle">
          <label className="block"><span className={rot}>Renovação Automática</span>
            <select value={f.renovacao ?? ""} onChange={(e) => set("renovacao", (e.target.value || null) as Ferramenta["renovacao"])} className={campo}>
              <option value="">—</option><option value="automatica">S</option><option value="manual">Não</option>
            </select>{naPlanilha("renovacao")}</label>
          <label className="flex items-center gap-2 pt-6 text-[13px]">
            <input type="checkbox" checked={f.teste} onChange={(e) => set("teste", e.target.checked)} />
            Teste ou projeto temporário
          </label>
          <label className="block"><span className={rot}>Término previsto</span>
            <input type="date" value={f.teste_ate ?? ""} onChange={(e) => set("teste_ate", txt(e.target.value))} className={campo} />
            <span className="mt-1 block text-[11.5px] text-tinta-fraca">
              Para projeto, teste ou migração. O Nexa avisa aos 30 e aos 2 dias, e depois da data se ela continuar ativa.
            </span></label>
          <label className="block sm:col-span-2"><span className={rot}>Observações</span>
            <textarea value={f.observacao ?? ""} onChange={(e) => set("observacao", txt(e.target.value))} rows={3} className={campo} /></label>
          <label className="block sm:col-span-2"><span className={rot}>Obs / Tendência (Painel Financeiro)</span>
            <input value={f.tendencia ?? ""} onChange={(e) => set("tendencia", txt(e.target.value))} className={campo} /></label>
        </Grupo>
      </div>

      {salvar.isError && (
        <p className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">{(salvar.error as FalhaApi).message}</p>
      )}
      {excluindo && ferramenta && (
        <div className="mt-4 rounded-lg border border-risco-linha bg-risco-bg px-4 py-3 text-[12.5px] text-risco-tinta">
          <p className="font-semibold">Excluir “{ferramenta.nome}”?</p>
          <p className="mt-1">
            A linha sai de todas as telas e contas, e a planilha não a traz de volta numa próxima importação. Use para a
            linha que não deveria existir (repetida, ou que não é cobrança). Para uma ferramenta que existiu e foi
            encerrada, escolha a situação Cancelada e salve. Dá para restaurar em Cancelados → Excluídas.
          </p>
          <label className="mt-2 block font-semibold">Por que sai?
            <textarea value={motivoExclusao} onChange={(e) => setMotivoExclusao(e.target.value)} rows={2} maxLength={500}
              placeholder="Ex.: linha repetida da planilha — a cobrança é a do Growth Plan"
              className="mt-1 w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[13px] font-normal text-tinta" />
          </label>
          {excluir.isError && <p className="mt-2">{(excluir.error as FalhaApi).message}</p>}
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" onClick={() => { setExcluindo(false); excluir.reset(); }} className={CLASSE_BOTAO_BARRA}>Voltar</button>
            <button type="button" disabled={!motivoExclusao.trim() || excluir.isPending} onClick={() => excluir.mutate()}
              className="rounded-lg bg-risco-tinta px-4 py-2 text-[13px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-45">
              {excluir.isPending ? "Excluindo…" : "Excluir"}
            </button>
          </div>
        </div>
      )}
      <div className="mt-5 flex justify-end gap-2">
        {ferramenta && !excluindo && (
          <button type="button" onClick={() => setExcluindo(true)}
            className="mr-auto rounded-lg px-2 py-2 text-[13px] font-semibold text-risco-tinta hover:underline">
            Excluir linha
          </button>
        )}
        <button type="button" onClick={onFechar} className={CLASSE_BOTAO_BARRA}>Cancelar</button>
        <button type="button" disabled={!f.nome.trim() || cartaoInvalido || salvar.isPending} onClick={() => salvar.mutate()}
          className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
          {salvar.isPending ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </Modal>
  );
}
